import React, { useEffect, useState, useContext } from 'react';
import { WelletContext } from '../../context/WelletContext';
import ContentApp from '../../components/ContentApp';
import Header from '../../components/Common/Header/Header';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CreateCardForm from './CreateCardForm';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../Common/Spinner/Spinner';
import SuccessScreen from '../../screens/Common/SuccessScreen';
import { useHistory } from 'react-router-dom';

const CreateCard = () => {
  const welletContext = useContext(WelletContext);
  const publicKey = useSelector((state) => state.app.publicKeyStripe);
  const language = useSelector((state) => state.app.language);
  const { t } = useTranslation();
  const history = useHistory();
  const [setupIntent, setSetupIntent] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [redirectToSuccess, setRedirectToSuccess] = useState(false);

  const getSaveCardIntent = async () => {
    try {
      const response = await welletContext.apis.tickets.get('/customer/setupIntent');
      const intentOptions = {
        clientSecret: response.data.clientSecret,
        locale: language,
        appearance: {
          // https://docs.stripe.com/elements/appearance-api#theme , https://docs.stripe.com/payments/payment-element
          theme: "night",
          labels: "floating",
          fontFamily: "Barlow, sans-serif",
          rules: {
            ".Input": {
              border: "1px solid #3d3d3d",
              color: "#fff",
              borderRadius: "10px",
              backgroundColor: "#121212",
            },
            ".Input:focus": {
              paddingTop: "0px",
              border: "1px solid #3d3d3d",
              boxShadow: "none",
            },
            ".Label--resting": {
              color: "#7e7e7e",
              fontWeight: 500,
            },
            ".Label--floating": {
              color: "#7e7e7e",
              fontWeight: "500",
              textShadow: "3px 0px 0px #000000, -3px 0 0px #000000",
            },
            ".AccordionItem": {
              border: "none",
              padding: "0px",
            },
            ".Block": {
              backgroundColor: "#121212",
            },
            ".Tab": {
              border: "1px solid #3d3d3d",
              color: "#898989",
            },
            ".Tab:focus": {
              border: "1px solid #1d9bf0",
              boxShadow: "none" ,
              backgroundColor: "#1d9bf0",
            },
            ".Tab--selected:active": {
              border: "1px solid #1d9bf0",
              boxShadow: "none" ,
              backgroundColor: "#1d9bf0",
            },
            ".Tab--selected": {
              border: "1px solid #1d9bf0",
              boxShadow: "none" ,
              backgroundColor: "#1d9bf0",
            },
            ".TabLabel": { color: "#7e7e7e" },
            ".TabLabel--selected": { color: "#ffffff" }
          },
          variables: {
            colorPrimary: '#1d9bf0',
            colorText: "#fff",
            colorBackground: "#000",
            colorDanger: "#f80005",
            fontSizeBase: "1rem",
            fontSizeSm: "0.875rem",
            fontWeightNormal: "400",
            colorIcon: "#fff",
            tabIconColor: "#7e7e7e",
            tabIconSelectedColor: '#ffffff'
          },
        },
      };
      if(response.data.accountId) {
        intentOptions.on_behalf_of = response.data.accountId;
      }
      setOptions(intentOptions);

      setSetupIntent(response.data);
    } catch (error) {
      console.error(error);
      setLoading(false);
      setError(error);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  useEffect(() => {
    if (options && publicKey && window.stripePromise === undefined) {
      window.stripePromise = loadStripe(publicKey);
      setStripeReady(true);
      setLoading(false);
    }
    else if (options && publicKey && window.stripePromise !== undefined) {
      setStripeReady(true);
      setLoading(false);
    }
  }, [options, publicKey]);

  useEffect(() => {
    getSaveCardIntent();
  }, []);

  return (
    <div>
      <Header showBack={true} title={t('addCard')} />
      <ContentApp>
        {loading && (
          <div
            className='d-flex justify-content-center align-items-center'
            style={{ height: '100vh' }}
          >
            <Spinner />
          </div>
        )}

        {successMessage && <div style={{ color: 'green' }}>{successMessage}</div>}

        {redirectToSuccess ? (
          <SuccessScreen
            title={`${t('done')}`}
            subtitle={t('cardRegisteredSuccessfully')}
            onClick={handleGoBack}
          />
        ) : (
          !loading &&
          stripeReady && (
            <Elements stripe={window.stripePromise} options={options}>
              <CreateCardForm
                onSuccess={() => {
                  setRedirectToSuccess(true);
                }}
                onError={(err) => setError(err)}
              />
            </Elements>
          )
        )}
      </ContentApp>
    </div>
  );
};

export default CreateCard;
